// src/components/Contact.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const GlobalContactCSS = () => (
  <style>{`
    :root{
      --text: #0f172a;
      --muted: rgba(15,23,42,.72);
      --accent: #06b6d4;  /* cyan */
      --accent2:#2563eb;  /* blue */
      --ring: rgba(6,182,212,.35);
      --border: rgba(255,255,255,.22);
      --shadow: 0 18px 50px rgba(2,6,23,.18);
    }

    html, body, #root{ height:100%; }
    body{
      margin:0;
      color:var(--text);
      font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
    }

    /* ✅ Fondo con carrusel full */
    .page-root{
      position: relative;
      min-height: 100vh;
      overflow: hidden;
    }

    .bg-slider{
      position: absolute;
      inset: 0;
      z-index: 0;
    }

    .bg-slider .slick-slider,
    .bg-slider .slick-list,
    .bg-slider .slick-track,
    .bg-slider .slick-slide > div{
      height: 100vh;
    }

    .bg-slide{
      position: relative;
      width: 100%;
      height: 100vh;
    }

    .bg-img{
      width:100%;
      height:100%;
      object-fit:cover;
      transform: scale(1.04);
      filter: saturate(1.05) contrast(1.02);
    }

    /* Overlay para legibilidad */
    .bg-overlay{
      position:absolute;
      inset:0;
      background:
        radial-gradient(900px 600px at 12% 10%, rgba(37,99,235,.25), transparent 55%),
        radial-gradient(900px 600px at 88% 12%, rgba(6,182,212,.28), transparent 50%),
        radial-gradient(900px 600px at 30% 90%, rgba(99,102,241,.18), transparent 55%),
        linear-gradient(180deg, rgba(0,0,0,.35), rgba(0,0,0,.60));
    }

    /* Contenido encima */
    .page-content{
      position: relative;
      z-index: 2;
      min-height: 100vh;
      display: grid;
      align-items: center;
      padding: clamp(18px, 4vw, 36px);
    }

    .wrap{
      width: min(1180px, 92vw);
      margin: 0 auto;
    }

    .grid{
      display:grid;
      gap: 14px;
      grid-template-columns: 1.15fr .85fr;
      align-items: start;
    }
    @media (max-width: 920px){
      .grid{ grid-template-columns: 1fr; }
    }

    /* Glass card (igual Home) */
    .card{
      background: rgba(255,255,255,.14);
      border: 1px solid var(--border);
      border-radius: 22px;
      box-shadow: var(--shadow);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      padding: clamp(16px, 3.2vw, 28px);
      color: #fff;
    }

    .topNav{
      display:flex;
      justify-content: space-between;
      align-items:center;
      gap: 10px;
      margin-bottom: 14px;
      flex-wrap: wrap;
    }

    .pill{
      display:inline-flex;
      padding: 8px 10px;
      border-radius: 999px;
      background: rgba(255,255,255,.12);
      border: 1px solid rgba(255,255,255,.18);
      font-weight: 900;
      color: rgba(255,255,255,.90);
      font-size: 12px;
    }

    .title{
      margin: 0 0 10px;
      font-size: clamp(22px, 3.2vw, 36px);
      font-weight: 900;
      letter-spacing: .2px;
      line-height: 1.05;
    }

    .title span{
      background: linear-gradient(90deg, var(--accent2), var(--accent));
      -webkit-background-clip: text;
      background-clip:text;
      color: transparent;
    }

    .lead{
      margin: 0 0 14px;
      font-size: 15px;
      line-height: 1.75;
      color: rgba(255,255,255,.86);
      max-width: 78ch;
    }

    .row{ display:grid; gap: 10px; }
    .row2{ display:grid; gap: 10px; grid-template-columns: 1fr 1fr; }
    @media (max-width: 720px){ .row2{ grid-template-columns: 1fr; } }

    .input, .textarea{
      width:100%;
      border-radius: 14px;
      border: 1px solid rgba(255,255,255,.18);
      background: rgba(255,255,255,.12);
      padding: 12px 12px;
      font-size: 15px;
      outline: none;
      color: rgba(255,255,255,.92);
    }
    .input::placeholder, .textarea::placeholder{ color: rgba(255,255,255,.65); }
    .textarea{ min-height: 120px; resize: vertical; }

    .input:focus, .textarea:focus{
      box-shadow: 0 0 0 4px rgba(6,182,212,.22);
      border-color: rgba(6,182,212,.55);
    }

    .btnRow{ display:flex; gap: 10px; flex-wrap: wrap; margin-top: 10px; }

    .btn{
      cursor:pointer;
      border: 0;
      border-radius: 14px;
      padding: 12px 14px;
      font-weight: 900;
      transition: transform .12s ease, filter .2s ease, opacity .2s ease;
      display:inline-flex;
      align-items:center;
      gap: 10px;
      user-select: none;
    }
    .btn:active{ transform: translateY(1px); }

    .btn-primary{
      background: linear-gradient(180deg, rgba(6,182,212,1), rgba(37,99,235,1));
      color:#062028;
      box-shadow: 0 12px 30px rgba(6,182,212,.18);
    }

    .btn-ghost{
      background: rgba(255,255,255,.14);
      color:#fff;
      border: 1px solid rgba(255,255,255,.22);
    }

    .btn:hover{ filter: brightness(1.05); }
    .btn[disabled]{ opacity:.55; cursor:not-allowed; }

    .hint{ font-size: 12px; color: rgba(255,255,255,.70); margin-top: 8px; }
    .ok{ font-weight: 900; color: #9dffc9; }
    .err{ font-weight: 900; color: #ffb4b4; }

    /* Slick */
    .bg-slider .slick-dots{
      bottom: 18px;
      z-index: 6;
    }
    .bg-slider .slick-dots li button:before{
      color: rgba(255,255,255,.55);
      opacity: 1;
      font-size: 10px;
    }
    .bg-slider .slick-dots li.slick-active button:before{
      color: rgba(6,182,212,1);
    }
    .bg-slider .slick-prev, .bg-slider .slick-next{
      z-index: 7;
      width: 44px;
      height: 44px;
      border-radius: 999px;
      background: rgba(255,255,255,.16);
      border: 1px solid rgba(255,255,255,.22);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }
    .bg-slider .slick-prev{ left: 16px; }
    .bg-slider .slick-next{ right: 16px; }
    .bg-slider .slick-prev:before, .bg-slider .slick-next:before{
      color: rgba(255,255,255,.9);
      opacity: 1;
      font-size: 20px;
    }

    .channelItem{
      padding: 10px 12px;
      border-radius: 16px;
      border: 1px solid rgba(255,255,255,.16);
      background: rgba(255,255,255,.10);
      margin-top: 10px;
    }
    .channelLabel{
      font-weight: 900;
      color: rgba(255,255,255,.9);
      margin: 0 0 4px;
      font-size: 13px;
    }
    .channelValue{
      margin: 0;
      color: rgba(255,255,255,.82);
      font-size: 14px;
      word-break: break-word;
    }

  `}</style>
);

export default function Contact() {
  const navigate = useNavigate();

  const slides = useMemo(
    () => [
      {
        src: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?fm=jpg&q=60&w=2400&ixlib=rb-4.0.3",
        alt: "Publicidad en vehículos",
      },
      {
        src: "https://media.istockphoto.com/id/612630478/photo/hong-kong-night-scene.jpg?s=2048x2048",
        alt: "Ciudad nocturna",
      },
      {
        src: "https://www.odedwagen.com/wp-content/uploads/revslider/sliderpreview_fullscreen/photodune-1717995-los-angeles-urban-city-at-sunset-with-freeway-trafic-m.jpg",
        alt: "Tráfico urbano al atardecer",
      },
    ],
    []
  );

  const settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      speed: 700,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 4200,
      arrows: true,
      pauseOnHover: false,
      pauseOnFocus: false,
    }),
    []
  );

  const EMAIL = "info@screendo.com";
  const PHONE = "+1 809-890-2516";
  const WHATSAPP_NUM = "18098902516"; // sin +, sin espacios

  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", mensaje: "" });
  const [status, setStatus] = useState({ type: "", text: "" });

  const canSend = useMemo(() => {
    const okEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    return form.nombre.trim() && okEmail && form.mensaje.trim().length >= 10;
  }, [form]);

  const handleSend = () => {
    setStatus({ type: "", text: "" });

    const nombre = form.nombre.trim();
    const email = form.email.trim();
    const tel = form.telefono.trim();
    const msg = form.mensaje.trim();

    const okEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!nombre) return setStatus({ type: "err", text: "Escribe tu nombre." });
    if (!okEmail) return setStatus({ type: "err", text: "Escribe un correo válido." });
    if (msg.length < 10) return setStatus({ type: "err", text: "El mensaje debe tener al menos 10 caracteres." });

    const subject = encodeURIComponent(`Contacto Screen DO — ${nombre}`);
    const body = encodeURIComponent(
      `Hola Screen DO,\n\n` +
        `Nombre: ${nombre}\n` +
        `Email: ${email}\n` +
        `Teléfono: ${tel || "N/A"}\n\n` +
        `Mensaje:\n${msg}\n`
    );

    window.location.href = `mailto:${EMAIL}?subject=${subject}&body=${body}`;
    setStatus({ type: "ok", text: "Listo: se abrió tu correo para enviar el mensaje." });
  };

  const openWhatsApp = () => {
    const nombre = form.nombre.trim() || "Cliente";
    const msg = form.mensaje.trim() || "Hola, quiero información sobre Screen DO.";
    const text = encodeURIComponent(`${nombre}: ${msg}`);
    window.open(`https://wa.me/${WHATSAPP_NUM}?text=${text}`, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <GlobalContactCSS />

      <div className="page-root">
        {/* Fondo con carrusel */}
        <div className="bg-slider" aria-hidden="true">
          <Slider {...settings}>
            {slides.map((s, i) => (
              <div key={i}>
                <div className="bg-slide">
                  <img className="bg-img" src={s.src} alt={s.alt || "Slide"} />
                  <div className="bg-overlay" />
                </div>
              </div>
            ))}
          </Slider>
        </div>

        {/* Contenido */}
        <div className="page-content">
          <div className="wrap">
            <div className="grid">
              {/* Form */}
              <div className="card">
                <div className="topNav">
                  <button className="btn btn-ghost" onClick={() => navigate("/")}>
                    ← Volver
                  </button>
                  <span className="pill">Soporte y ventas</span>
                </div>

                <h2 className="title">
                  <span>Contacto</span>
                </h2>

                <p className="lead">
                  Cuéntanos qué necesitas y te responderemos lo antes posible. También puedes escribirnos por WhatsApp.
                </p>

                <div className="row2">
                  <input
                    className="input"
                    placeholder="Tu nombre"
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  />
                  <input
                    className="input"
                    placeholder="Tu correo"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>

                <div className="row" style={{ marginTop: 10 }}>
                  <input
                    className="input"
                    placeholder="Teléfono (opcional)"
                    value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  />
                  <textarea
                    className="textarea"
                    placeholder="Escribe tu mensaje..."
                    value={form.mensaje}
                    onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
                  />
                </div>

                <div className="btnRow">
                  <button className="btn btn-primary" onClick={handleSend} disabled={!canSend}>
                    Enviar por correo
                  </button>
                  <button className="btn btn-ghost" onClick={openWhatsApp}>
                    Enviar por WhatsApp
                  </button>
                  <button className="btn btn-ghost" onClick={() => (window.location.href = `mailto:${EMAIL}`)}>
                    Abrir correo
                  </button>
                </div>

                {status.text && (
                  <div className={status.type === "ok" ? "ok" : "err"} style={{ marginTop: 10 }}>
                    {status.text}
                  </div>
                )}

                <div className="hint">
                  * Para enviar por correo, tu navegador abrirá tu app de correo predeterminada.
                </div>
              </div>

              {/* Canales */}
              <div className="card">
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <h3 style={{ margin: 0, fontWeight: 900 }}>Canales</h3>
                  <button className="btn btn-primary" onClick={() => (window.location.href = `mailto:${EMAIL}`)}>
                    Escribir ahora
                  </button>
                </div>

                <div className="channelItem">
                  <p className="channelLabel">Email</p>
                  <p className="channelValue">{EMAIL}</p>
                </div>

                <div className="channelItem">
                  <p className="channelLabel">Teléfono</p>
                  <p className="channelValue">{PHONE}</p>
                </div>

                <div className="channelItem">
                  <p className="channelLabel">Horario</p>
                  <p className="channelValue">Lunes a viernes (9:00 AM – 6:00 PM)</p>
                </div>

                <div className="hint" style={{ marginTop: 12 }}>
                  Si prefieres WhatsApp, escribe tu mensaje y presiona <strong>“Enviar por WhatsApp”</strong>.
                </div>
              </div>
            </div>

            {/* espacio extra para que en mobile no tape los dots */}
            <div style={{ height: 24 }} />
          </div>
        </div>
      </div>
    </>
  );
}
